import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useEffect, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { Landmark, Pencil, Save, RotateCcw, ArrowLeft } from "lucide-react";

const API_BASE = "https://nissi-india.com/api";

const accountTypeIdMap = { Savings: 1, Current: 2 };
const accountTypeNameMap = { 1: "Savings", 2: "Current" };

const SupplierBankAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor } = currentSkin;

  // ✅ FIX: supplierid now comes from navigation state (same pattern as
  // SupplierMainMasEdit.jsx), instead of being hardcoded to 1. Every
  // bank detail inserted from this page was previously attached to
  // supplier ID 1 regardless of which supplier you were actually editing.
  const supplier = location.state?.supplier || null;
  const supplierId =
    supplier?.fld_suppliermainid ||
    supplier?.fld_supplierid ||
    location.state?.supplierid ||
    "";

  const [bankName, setBankName] = useState("");
  const [branch, setBranch] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [holderName, setHolderName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [editId, setEditId] = useState(null);
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdHeader = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdHeader,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdHeader, branchType, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const resetForm = () => {
    setBankName("");
    setBranch("");
    setIfscCode("");
    setAccountNumber("");
    setAccountType("");
    setHolderName("");
    setRemarks("");
    setEditId(null);
    setFormError("");
  };

  // ✅ FIX: was calling /getSupplierBankDetailById?detailid=5 — a single
  // hardcoded record — so newly inserted rows never appeared. This now
  // calls /bindSupplierBank?supplierid=... (the same list endpoint
  // SupplierMainMasEdit.jsx already uses for its Bank tab) to fetch every
  // bank detail belonging to the current supplier.
  const fetchTableData = async () => {
    if (!supplierId) {
      setTableData([]);
      return;
    }
    setTableLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/bindSupplierBank`, {
        params: { supplierid: supplierId },
        headers: commonHeaders,
      });
      setTableData(res.data?.bankList || []);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load bank details");
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSubmit = async () => {
    setFormError("");

    if (!supplierId) {
      setFormError("Supplier is missing — please reopen this page from the supplier edit screen.");
      return;
    }
    if (!bankName || !branch || !accountNumber || !accountType) {
      setFormError("Please fill all required fields.");
      return;
    }

    const payload = {
      bankname: bankName,
      bankbranch: branch,
      ifsc: ifscCode,
      accounttypeid: accountTypeIdMap[accountType],
      accountnumber: accountNumber,
      holdername: holderName,
      remarks,
    };

    setLoading(true);
    try {
      if (editId) {
        await axios.put(
          `${API_BASE}/updateSupplierBankDetail`,
          { ...payload, detailid: editId },
          { headers: commonHeaders }
        );
        toast.success("Bank detail updated successfully");
      } else {
        await axios.post(
          `${API_BASE}/addSupplierBankDetail`,
          { ...payload, supplierid: supplierId },
          { headers: commonHeaders }
        );
        toast.success("Bank detail added successfully");
      }

      resetForm();
      fetchTableData();
    } catch (err) {
      console.error("Error submitting bank details:", err);
      const serverMsg = err.response?.data?.error || err.response?.data?.message;
      setFormError(serverMsg || "Error saving bank detail.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setBankName(item.fld_bankname || "");
    setBranch(item.fld_bankbranch || "");
    setIfscCode(item.fld_IFSCCode || item.fld_ifsc || "");
    setAccountNumber(item.fld_accountnumber || "");
    setAccountType(accountTypeNameMap[item.fld_accounttypeid] || "");
    setHolderName(item.fld_holdername || "");
    setRemarks(item.fld_remarks || "");
    setEditId(item.fld_detailid || item.fld_bankid);
    setFormError("");
  };

  useEffect(() => {
    if (!supplierId) {
      toast.error("Supplier not found. Please open this page from the supplier edit screen.");
      return;
    }
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Supplier Bank Details</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header + Back */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <Landmark className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Bank Details</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {supplier?.fld_suppliername
                  ? `Manage bank accounts for ${supplier.fld_suppliername}`
                  : "Manage supplier bank accounts"}
              </p>
            </div>
          </div>

          <button
  onClick={() =>
    navigate("/inventory/Supplier-Main-MasEdit", { state: { supplierid: supplierId } })
  }
  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
    isDark
      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
      : "border-slate-300 text-slate-700 hover:bg-slate-100"
  }`}
>
  <ArrowLeft size={16} />
  Back
</button>
        </div>

        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div
            className={`px-6 py-4 border-b flex items-center gap-3 ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className={`w-3 h-8 rounded-full bg-${baseColor}-500`}></div>
            <div>
              <h2 className="text-lg font-bold">
                {editId ? "Edit Bank Detail" : "Add Bank Detail"}
              </h2>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {editId ? "Update the selected bank account" : "Enter the supplier's bank account details"}
              </p>
            </div>
          </div>

          <div className="p-6">
            {formError && (
              <div
                className={`flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-lg border text-sm font-medium ${
                  isDark
                    ? "bg-red-900/30 border-red-500/40 text-red-300"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                <span>{formError}</span>
                <button
                  type="button"
                  onClick={() => setFormError("")}
                  className="shrink-0 opacity-70 hover:opacity-100"
                  aria-label="Dismiss error"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                     IFSC Code <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={ifscCode}
                    onChange={(e) => setIfscCode(e.target.value)}
                    className={inputClass}
                    placeholder="Enter IFSC code"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className={inputClass}
                    placeholder="Enter account number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    className={`${inputClass} resize-none`}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    className={inputClass}
                    placeholder="Enter branch name"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Account Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Account Type</option>
                    <option value="Savings">Savings</option>
                    <option value="Current">Current</option>
                  </select>
                </div>

               <div>
  <label className={labelClass}>
    Holder Name <span className="text-red-500">*</span>
  </label>
  <input
    type="text"
    value={holderName}
    onChange={(e) => setHolderName(e.target.value)}
    className={inputClass}
    placeholder="Enter account holder name"
  />
</div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              {editId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <RotateCcw size={16} />
                  Cancel Edit
                </button>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
              >
                <Save size={16} />
                {loading ? "Saving..." : editId ? "Update Bank Detail" : "Save Bank Detail"}
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-500`}></div>
              <h3 className="text-xl font-bold">Bank Details List</h3>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Bank Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Branch</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Account Number</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Account Type</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Edit</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {tableLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : tableData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-500">
                      No bank details found for this supplier
                    </td>
                  </tr>
                ) : (
                  tableData.map((item, index) => (
                    <tr
                      key={item.fld_detailid || item.fld_bankid || index}
                      className={`transition ${
                        isDark ? "bg-gray-800/20 hover:bg-gray-700/30" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3">{index + 1}</td>
                      <td className="px-4 py-3 font-medium">{item.fld_bankname}</td>
                      <td className="px-4 py-3">{item.fld_Bankbranch || item.fld_bankbranch}</td>
                      <td className="px-4 py-3">{item.fld_accountnumber}</td>
                      <td className="px-4 py-3">{accountTypeNameMap[item.fld_accounttypeid] || "-"}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(item)}
                          className={`p-1.5 rounded-full transition ${
                            isDark
                              ? "bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50"
                              : "bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                          }`}
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default SupplierBankAdd;